import { Q, Query, Relation } from "@nozbe/watermelondb";
import { Associations } from "@nozbe/watermelondb/Model";
import {
  children,
  field,
  immutableRelation,
  text,
  writer,
  lazy,
} from "@nozbe/watermelondb/decorators";
import { BaseModel } from "./Base";
import Product from "./Product";
import StockCount from "./StockCount";
import StockCountItem from "./StockCountItem";
import { combineLatest, from } from "rxjs";
import { switchMap } from "@nozbe/watermelondb/utils/rx";
import { some } from "lodash";
import { SyncState } from "../constants";

export default class StockCountLine extends BaseModel {
  static table = "stock_count_lines";

  static associations: Associations = {
    stock_count: { type: "belongs_to", key: "stock_count_id" },
    stock_count_items: {
      type: "has_many",
      foreignKey: "stock_count_line_id",
    },
  };

  @immutableRelation("products", "product_id") product: Relation<Product>;

  @text("barcode") barcode!: string;
  @field("initial_expected") initialExpected: number | null;
  @field("expected") expected: number | null;
  @field("name") name: string | null;

  @immutableRelation("stock_counts", "stock_count_id")
  stockCount: Relation<StockCount>;

  @children("stock_count_items")
  items: Query<StockCountItem>;

  @writer async triggerUpdate() {
    await this.update();
  }

  @writer async setName(name: string | null) {
    await this.update((c) => {
      c.name = name;
    });
  }

  @writer async setExpected(expected: number | null) {
    await this.update((c) => {
      c.expected = expected;
    });
  }

  @lazy counted = from(
    combineLatest(
      //@ts-ignore
      this.items.observeWithColumns(["counted"])
    ).pipe(
      switchMap(async ([items]) => {
        let counted = 0;
        for (const item of items) {
          counted += item.counted || 0;
        }
        return counted;
      })
    )
  );

  @lazy isSyncing = from(
    combineLatest(
      //@ts-ignore
      this.items.observeWithColumns(["sync_state"])
    ).pipe(
      switchMap(async ([items]) => {
        return some(items, (item) => item.syncState === SyncState.UPLOAD);
      })
    )
  );

  @lazy syncFailed = from(
    combineLatest(
      //@ts-ignore
      this.items.observeWithColumns(["sync_state", "num_sync_attempts"])
    ).pipe(
      switchMap(async ([items]) => {
        return some(
          items,
          (item) =>
            item.syncState == SyncState.UPLOAD && item.numSyncAttempts >= 1
        );
      })
    )
  );

  async getFailedExports() {
    let failedExports = [];

    const items = await this.items.fetch();

    for (const item of items) {
      const hasFailedExport = await item.hasExportFailed();
      if (hasFailedExport) {
        failedExports.push(item);
      }
    }

    return failedExports;
  }
}
